import React, { useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useApp } from '@/hooks/use-app-context';
import { useLanguages } from '@/hooks/use-languages';
import { useToast } from '@/hooks/use-toast';
import { useSessionDeepLink } from '@/hooks/use-session-deeplink';
import { ttsAssessmentApi } from '@/api/tts-assessment.api';
import { transcriptionFrenchApi } from '@/api/transcriptionFrench.api';
import { lomBenchApi } from '@/api/lombench.api';
import { progressApi } from '@/api/progress.api';
import { getMediaRecorderOptions } from '@/config/audio.config';
import { calculateWER, calculateCER, calculateDiff } from '@/utils/metrics';
import { extractErrorMessage, isAssessmentTask, parseCsvLines } from './model-performance.helpers';
import { ModelsPageHeader } from '@/components/admin/admin-tabs';
import { ModelPerformanceSetup } from './ModelPerformanceSetup';
import { TTSScreen } from './TTSScreen';
import { ASRScreen } from './ASRScreen';
import type { AssessmentTask, ModelItem } from './model-performance.types';


export default function ModelPerformance() {
  const { t, user, language } = useApp();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const tModel = t.modelPerformancePage;
  const linkedTask = searchParams.get('task') || '';
  const linkedModel = searchParams.get('model') || '';
  const linkedLanguage = searchParams.get('language') || '';

  const [selectedLanguage, setSelectedLanguage] = useState(linkedLanguage);
  const [selectedModel, setSelectedModel] = useState(linkedModel);
  const [selectedTask, setSelectedTask] = useState<AssessmentTask>(isAssessmentTask(linkedTask) ? linkedTask : 'TTS');
  const [sessionStarted, setSessionStarted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [ttsRotatedPhrases, setTtsRotatedPhrases] = useState<Array<{ fr: string; text: string }>>([]);
  const [currentTtsIndex, setCurrentTtsIndex] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalTtsAssessments, setTotalTtsAssessments] = useState(0);
  const [isGeneratingTts, setIsGeneratingTts] = useState(false);
  const [currentTtsText, setCurrentTtsText] = useState('');
  const [generatedAudioUrl, setGeneratedAudioUrl] = useState('');
  const [referenceAudioUrl, setReferenceAudioUrl] = useState('');
  const [qualityRating, setQualityRating] = useState<number | null>(null);
  const [naturalness, setNaturalness] = useState<number | null>(null);
  const [pronunciation, setPronunciation] = useState<number | null>(null);
  const [ttsComments, setTtsComments] = useState('');
  const [globalProgressIndex, setGlobalProgressIndex] = useState(0);

  const [asrInputMode, setAsrInputMode] = useState<'text' | 'audio' | 'record'>('record');
  const [asrInputText, setAsrInputText] = useState('');
  const [asrAudioUrl, setAsrAudioUrl] = useState('');
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [transcriptionResult, setTranscriptionResult] = useState('');
  const [referenceText, setReferenceText] = useState('');
  const [wer, setWer] = useState<number | null>(null);
  const [cer, setCer] = useState<number | null>(null);
  const [diffSegments, setDiffSegments] = useState<Array<{ type: string; original: string; generated: string }>>([]);

  const [isRecording, setIsRecording] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);

  const { activeLanguages, isLoading: isLoadingLanguages } = useLanguages();

  const [models, setModels] = useState<ModelItem[]>([
    { name: 'Umbaji TTS v1.0', task: 'TTS' },
    { name: 'Umbaji TTS v1.2', task: 'TTS' },
    { name: 'Umbaji ASR v1.0', task: 'ASR' },
    { name: 'Umbaji ASR v1.5', task: 'ASR' },
  ]);

  React.useEffect(() => {
    const loadModels = async () => {
      try {
        const [ttsModels, asrModels] = await Promise.all([
          lomBenchApi.findByType('TTS'),
          lomBenchApi.findByType('ASR'),
        ]);
        const mapped = [
          ...ttsModels.map((m) => ({ name: `${m.name} v${m.version}`, task: 'TTS' as AssessmentTask, id: m._id })),
          ...asrModels.map((m) => ({ name: `${m.name} v${m.version}`, task: 'ASR' as AssessmentTask, id: m._id })),
        ];
        if (mapped.length > 0) setModels(mapped);
      } catch (error) {
        console.error('Failed to load models from LomBench:', error);
      }
    };
    loadModels();
  }, []);

  const loadSamples = async () => {
    if (!selectedLanguage || !selectedModel || !selectedTask) {
      toast({ title: t.error, description: tModel.fillAllFields, variant: 'destructive' });
      return;
    }
    setIsLoading(true);
    setLoadError(null);
    try {
      if (selectedTask === 'TTS') {
        let savedProgress = 0;
        let pageToLoad = 1;
        if (user?.id) {
          try {
            const progressData = await progressApi.getUserProgressByLang(selectedLanguage);
            savedProgress = progressData.last_index && progressData.last_index >= 0 ? progressData.last_index : 0;
            setGlobalProgressIndex(savedProgress);
            pageToLoad = Math.floor(savedProgress / 10) + 1;
            setCurrentPage(pageToLoad);
            setCurrentTtsIndex(savedProgress % 10);
          } catch {
            savedProgress = 0;
            pageToLoad = 1;
            setCurrentTtsIndex(0);
            setGlobalProgressIndex(0);
          }
        }
        setCurrentPage(pageToLoad);
        const response = await transcriptionFrenchApi.getRotatedFrenchPhrases({ page: pageToLoad, limit: 10 });
        if (!response.data || response.data.length === 0) {
          if (pageToLoad > 1) {
            const retryResponse = await transcriptionFrenchApi.getRotatedFrenchPhrases({ page: 1, limit: 10 });
            if (retryResponse.data && retryResponse.data.length > 0) {
              response.data = retryResponse.data;
              pageToLoad = 1;
              setCurrentPage(1);
              setCurrentTtsIndex(0);
              setGlobalProgressIndex(0);
              if (user?.id) await progressApi.updateUserProgress(selectedLanguage, 0, 1);
            } else {
              toast({ title: t.error, description: tModel.noPhraseAvailableDb, variant: 'destructive' });
              setIsLoading(false);
              return;
            }
          } else {
            toast({ title: t.error, description: tModel.noPhraseAvailableTotal.replace('{total}', String(response.total || 0)), variant: 'destructive' });
            setIsLoading(false);
            return;
          }
        }
        const parsed = parseCsvLines(response.data || []);
        setTtsRotatedPhrases(parsed);
        if (parsed.length > 0) {
          const localIndex = savedProgress % 10;
          const phraseToShow = parsed[Math.min(localIndex, parsed.length - 1)];
          if (phraseToShow) setCurrentTtsText(phraseToShow.fr || phraseToShow.text);
          else { setCurrentTtsText(parsed[0].fr || parsed[0].text); setCurrentTtsIndex(0); }
        } else {
          toast({ title: tModel.warningTitle, description: tModel.noValidPhraseAfterFilter, variant: 'destructive' });
          setIsLoading(false);
          return;
        }
      } else {
        setAsrInputMode('record');
        setAsrInputText('');
        setAsrAudioUrl('');
        setTranscriptionResult('');
        setWer(null);
        setCer(null);
        setDiffSegments([]);
      }
      setSessionStarted(true);
      toast({ title: tModel.loadSuccessTitle, description: tModel.loadSuccessDescription.replace('{model}', selectedModel) });
    } catch (error: unknown) {
      const description = extractErrorMessage(error) || tModel.loadingError;
      setLoadError(description);
      toast({ title: t.error, description, variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  };

  const { startLinkedSession } = useSessionDeepLink({
    searchParams, setSearchParams, sessionValue: 'model-performance',
    selectedLanguage, setSelectedLanguage, sessionStarted, isStartingSession: isLoading,
    startSession: loadSamples, canResume: Boolean(selectedLanguage && selectedModel && selectedTask),
    params: { task: selectedTask, model: selectedModel },
  });

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, getMediaRecorderOptions());
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      mediaRecorder.ondataavailable = (event) => { if (event.data.size > 0) audioChunksRef.current.push(event.data); };
      mediaRecorder.onstop = () => {
        const mimeType = mediaRecorder.mimeType || 'audio/wav';
        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
        setAsrAudioUrl(URL.createObjectURL(audioBlob));
        stream.getTracks().forEach((track) => track.stop());
      };
      mediaRecorder.start();
      setIsRecording(true);
    } catch {
      toast({ title: t.error, description: String(tModel.microphoneError ?? ''), variant: 'destructive' });
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) { mediaRecorderRef.current.stop(); setIsRecording(false); }
  };

  const generateTtsAudio = async () => {
    if (!user?.id || !selectedModel || !currentTtsText) return;
    const modelData = models.find((m) => m.name === selectedModel);
    if (!modelData?.id) return;
    setIsGeneratingTts(true);
    try {
      const result = await lomBenchApi.ttsGenerate({ modelId: modelData.id, lang: selectedLanguage, text: currentTtsText });
      setGeneratedAudioUrl(result.hypothesisAudioUrl);
      setReferenceAudioUrl(result.referenceAudioUrl || '');
      toast({ title: String(tModel.audioGeneratedSuccess ?? ''), description: String(tModel.audioGeneratedDesc ?? '') });
    } catch (error: unknown) {
      const axiosData = (error as any)?.response?.data;
      const serverMsg = typeof axiosData?.message === 'string' ? axiosData.message : null;
      const description = serverMsg === 'Model not TTS or lang not supported'
        ? String(tModel.modelLangNotSupported ?? tModel.audioGenerationError)
        : serverMsg || extractErrorMessage(error) || tModel.audioGenerationError;
      toast({ title: t.error, description, variant: 'destructive' });
    } finally {
      setIsGeneratingTts(false);
    }
  };

  const transcribeAudio = async () => {
    if (!user?.id || !selectedModel) return;
    const modelData = models.find((m) => m.name === selectedModel);
    if (!modelData?.id) return;
    if (asrInputMode === 'text' && !asrInputText) return;
    if ((asrInputMode === 'audio' || asrInputMode === 'record') && !asrAudioUrl) return;
    setIsTranscribing(true);
    try {
      let audioUrl = asrAudioUrl;
      if (asrInputMode === 'text') {
        setReferenceText(asrInputText);
        const ttsResult = await lomBenchApi.ttsGenerate({ modelId: modelData.id, lang: selectedLanguage, text: asrInputText });
        audioUrl = ttsResult.hypothesisAudioUrl;
        setAsrAudioUrl(audioUrl);
      } else if (asrInputMode === 'record') {
        setReferenceText(asrInputText);
      }
      if (audioUrl.startsWith('blob:')) {
        try {
          const blob = await fetch(audioUrl).then((r) => r.blob());
          const ext = blob.type.split('/')[1]?.split(';')[0] || 'wav';
          const result = await lomBenchApi.asrTranscribeFile(blob, `audio.${ext}`, String(modelData.id), selectedLanguage);
          setTranscriptionResult(result.hypothesisText);
          const ref = asrInputMode === 'record' ? asrInputText : referenceText;
          if (ref) { setWer(calculateWER(ref, result.hypothesisText)); setCer(calculateCER(ref, result.hypothesisText)); setDiffSegments(calculateDiff(ref, result.hypothesisText)); }
          toast({ title: String(tModel.transcriptionSuccess ?? ''), description: String(tModel.transcriptionSuccessDesc ?? '') });
          setIsTranscribing(false);
          return;
        } catch (uploadError) {
          toast({ title: t.error, description: String(tModel.fileTranscriptionError ?? ''), variant: 'destructive' });
          setIsTranscribing(false);
          return;
        }
      }
      const result = await lomBenchApi.asrTranscribe({ modelId: String(modelData.id), lang: selectedLanguage, audioUrl });
      setTranscriptionResult(result.hypothesisText);
      const ref = asrInputMode === 'record' ? asrInputText : referenceText;
      if (ref) { setWer(calculateWER(ref, result.hypothesisText)); setCer(calculateCER(ref, result.hypothesisText)); setDiffSegments(calculateDiff(ref, result.hypothesisText)); }
      toast({ title: String(tModel.transcriptionSuccess ?? ''), description: String(tModel.transcriptionSuccessDesc ?? '') });
    } catch {
      toast({ title: t.error, description: String(tModel.transcriptionError ?? ''), variant: 'destructive' });
    } finally {
      setIsTranscribing(false);
    }
  };

  const submitTtsAssessment = async () => {
    if (qualityRating === null || naturalness === null || pronunciation === null) {
      toast({ title: t.error, description: tModel.rateAllCriteria, variant: 'destructive' });
      return;
    }
    if (!generatedAudioUrl) return;
    try {
      if (!user?.id) throw new Error(tModel.userRequired);
      const modelData = models.find((m) => m.name === selectedModel);
      if (!modelData?.id) throw new Error('ID du modèle introuvable');
      const averageScore = ((qualityRating + naturalness + pronunciation) / 3).toFixed(1);
      await lomBenchApi.createEvaluation({
        type: 'TTS', lang: selectedLanguage, modelId: modelData.id, metric: 'MOS', score: 0,
        details: { qualityRating, naturalness, pronunciation, comments: ttsComments },
        hypothesisAudioUrl: generatedAudioUrl, referenceAudioUrl: referenceAudioUrl || undefined,
      });
      setTotalTtsAssessments((prev) => prev + 1);
      toast({ title: tModel.assessmentSavedTitle, description: tModel.assessmentSavedTtsDescription.replace('{score}', averageScore) });
      moveToNextTts();
    } catch (error: unknown) {
      const description = extractErrorMessage(error) || tModel.saveFailed;
      toast({ title: t.error, description, variant: 'destructive' });
    }
  };

  const moveToNextTts = async () => {
    setQualityRating(null); setNaturalness(null); setPronunciation(null); setTtsComments(''); setGeneratedAudioUrl(''); setReferenceAudioUrl('');
    const nextLocalIndex = currentTtsIndex + 1;
    const nextGlobalIndex = globalProgressIndex + 1;
    if (user?.id) {
      try { await progressApi.updateUserProgress(selectedLanguage, nextGlobalIndex, 1); setGlobalProgressIndex(nextGlobalIndex); } catch { /* noop */ }
    }
    if (nextLocalIndex >= ttsRotatedPhrases.length) {
      const completedCount = totalTtsAssessments + 1;
      toast({ title: String(tModel.pageCompleteTitle ?? ''), description: String(tModel.pageCompleteDesc?.replace('{count}', String(completedCount)) ?? '') });
      try {
        const nextBackendPage = currentPage + 1;
        const response = await transcriptionFrenchApi.getRotatedFrenchPhrases({ page: nextBackendPage, limit: 10 });
        const parsed = parseCsvLines(response.data || []);
        if (parsed.length > 0) {
          setTtsRotatedPhrases(parsed); setCurrentPage(nextBackendPage); setCurrentTtsIndex(0); setCurrentTtsText(parsed[0].fr || parsed[0].text);
          toast({ title: String(tModel.newPageLoaded ?? ''), description: String(tModel.newPageLoadedDesc?.replace('{page}', String(nextBackendPage)) ?? '') });
        } else {
          toast({ title: tModel.sessionCompleteTitle, description: String(tModel.sessionCompleteDesc?.replace('{count}', String(completedCount)) ?? '') });
          navigate('/' + (language || 'fr') + '/admin/config/models');
        }
      } catch {
        toast({ title: t.error, description: String(tModel.nextPageError ?? ''), variant: 'destructive' });
        navigate('/' + (language || 'fr') + '/admin/config/models');
      }
    } else {
      setCurrentTtsIndex(nextLocalIndex);
      const nextPhrase = ttsRotatedPhrases[nextLocalIndex];
      if (nextPhrase) setCurrentTtsText(nextPhrase.fr || nextPhrase.text);
    }
  };


  const resetAsr = () => {
    setAsrInputText(''); setAsrAudioUrl(''); setTranscriptionResult(''); setReferenceText(''); setWer(null); setCer(null); setDiffSegments([]);
  };

  // The testing bench is the third tab of "AI models": same title and tabs
  // as the other two, during the setup and during a session.
  const framed = (content: React.ReactNode, description?: string) => (
    <div className="mx-auto w-full max-w-7xl space-y-6">
      <ModelsPageHeader description={description} />
      {content}
    </div>
  );

  if (!sessionStarted) {
    return framed(
      <ModelPerformanceSetup
        selectedTask={selectedTask} selectedModel={selectedModel} selectedLanguage={selectedLanguage}
        models={models} loadError={loadError} isLoading={isLoading} isLoadingLanguages={isLoadingLanguages}
        activeLanguages={activeLanguages} onSelectTask={setSelectedTask} onSelectModel={setSelectedModel}
        onSelectLanguage={setSelectedLanguage} onStart={startLinkedSession} onRetry={loadSamples}
        onClearError={() => setLoadError(null)} onGoHome={() => navigate('/' + (language || 'fr') + '/admin/config/models')}
      />,
      String(t.modelPerformanceDescription ?? ''),
    );
  }

  if (selectedTask === 'TTS') {
    return framed(
      <TTSScreen
        language={selectedLanguage} model={selectedModel}
        currentTtsIndex={currentTtsIndex} totalPhrases={ttsRotatedPhrases.length} currentPage={currentPage}
        currentTtsText={currentTtsText} generatedAudioUrl={generatedAudioUrl} referenceAudioUrl={referenceAudioUrl}
        isGeneratingTts={isGeneratingTts} qualityRating={qualityRating} naturalness={naturalness}
        pronunciation={pronunciation} ttsComments={ttsComments} totalTtsAssessments={totalTtsAssessments}
        onSetQualityRating={setQualityRating} onSetNaturalness={setNaturalness} onSetPronunciation={setPronunciation}
        onSetTtsComments={setTtsComments} onGenerateTts={generateTtsAudio} onSubmit={submitTtsAssessment}
        onPrev={() => currentTtsIndex > 0 && setCurrentTtsIndex((prev) => prev - 1)}
        onBack={() => navigate('/' + (language || 'fr') + '/admin/config/models')}
      />,
    );
  }

  return framed(
    <ASRScreen
      language={selectedLanguage} model={selectedModel}
      asrInputMode={asrInputMode} asrInputText={asrInputText} asrAudioUrl={asrAudioUrl}
      isTranscribing={isTranscribing} transcriptionResult={transcriptionResult} referenceText={referenceText}
      wer={wer} cer={cer} diffSegments={diffSegments} isRecording={isRecording}
      onSetAsrInputMode={setAsrInputMode} onSetAsrInputText={setAsrInputText} onSetAsrAudioUrl={setAsrAudioUrl}
      onStartRecording={startRecording} onStopRecording={stopRecording} onTranscribe={transcribeAudio}
      onReset={resetAsr} onBack={() => navigate('/' + (language || 'fr') + '/admin/config/models')}
    />,
  );
}
